// Tiny inline-SVG sparkline. ~80 lines, zero deps.
//
// Renders one polyline over a fixed-aspect viewbox. Caller passes
// values in any unit; the component normalizes to viewbox-space.
// `fillBelow` shades the area under the curve in a translucent
// hue — useful for "growth over time" framing.

interface SparklineProps {
  values: number[];
  // Width and height in px. Defaults sized for inline use beside
  // the existing metric cards.
  width?: number;
  height?: number;
  // CSS color string (any valid CSS value). Pass a Tailwind theme
  // var like `var(--color-theia-500)` for theme-coupled tints, or
  // a literal hex/rgb when you need something one-off.
  color?: string;
  fillBelow?: boolean;
  // Optional explicit min/max to lock the y-axis across multiple
  // sparklines (so growth in one isn't masked by a higher peak in
  // another). Defaults to data range.
  yMin?: number;
  yMax?: number;
  ariaLabel?: string;
}

export function Sparkline({
  values,
  width = 160,
  height = 36,
  color = 'var(--color-theia-500)',
  fillBelow = true,
  yMin,
  yMax,
  ariaLabel,
}: SparklineProps) {
  if (values.length === 0) {
    return (
      <svg width={width} height={height} role="img" aria-label={ariaLabel ?? 'no data'}>
        <line
          x1={0}
          y1={height / 2}
          x2={width}
          y2={height / 2}
          stroke="var(--color-neutral-500)"
          strokeDasharray="2 3"
          opacity={0.5}
        />
      </svg>
    );
  }
  const min = yMin ?? Math.min(...values);
  const max = yMax ?? Math.max(...values);
  const span = max - min === 0 ? 1 : max - min;
  // Single-value series renders as a flat midline.
  const points = values.map((v, i) => {
    const x = values.length === 1 ? width / 2 : (i / (values.length - 1)) * width;
    const y = height - ((v - min) / span) * height;
    return [x, y] as const;
  });
  const polyline = points.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const areaPath = (() => {
    if (!fillBelow || points.length === 0) return null;
    const first = points[0];
    const last = points[points.length - 1];
    if (!first || !last) return null;
    return (
      `M ${first[0].toFixed(1)},${height} ` +
      `L ${points.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' L ')} ` +
      `L ${last[0].toFixed(1)},${height} Z`
    );
  })();
  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={ariaLabel ?? 'trend'}
      style={{ display: 'block' }}
    >
      {areaPath && (
        <path d={areaPath} fill={color} opacity={0.18} />
      )}
      <polyline
        points={polyline}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        strokeLinejoin="round"
      />
    </svg>
  );
}
